import { type FieldValues, useController } from 'react-hook-form'
import {
  type ComponentProps,
  type ComponentRef,
  forwardRef,
  type Ref,
  useImperativeHandle,
  useRef,
} from 'react'
import { BirthdateInput } from '@/src/common/components/Input/InputBirthdate'
import { useEvent } from 'tamagui'
import type { TextInput } from 'react-native'
import { useSardine } from '@/src/services/Sardine'
import { sardineIdKey } from '@/src/services/Sardine/SardineId'
import type {
  FormInputProps,
  FormInputType,
} from '@/src/experiences/card-application/components/form-input'

// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
export const FormInputDateOfBirth = forwardRef(function FormInputDateOfBirth<T extends FieldValues>(
  { control, name, sardineId, onFocus, onBlur, ...props }: FormInputProps<T>,
  ref: Ref<TextInput | null>,
) {
  const { trackTextChange, trackFocusChange } = useSardine()
  const { field, fieldState, formState } = useController({ control, name })
  const localRef = useRef<TextInput>(null)
  const info = props.info ?? {}

  // @ts-expect-error type differences between Ref.current and Ref
  // going to ignore for now
  useImperativeHandle(ref, () => localRef.current)
  useImperativeHandle(field.ref, () => localRef.current)

  const handleChange: NonNullable<ComponentProps<typeof BirthdateInput>['onChange']> = useEvent(
    (date) => {
      field.onChange(date)
      if (sardineId) {
        void trackTextChange(sardineId, date?.toISOString() ?? '')
      }
    },
  )

  const handleFocus: NonNullable<ComponentProps<typeof BirthdateInput>['onFocus']> = useEvent(
    (...args) => {
      if (sardineId) {
        void trackFocusChange(sardineId, true)
      }
      onFocus?.(...args)
    },
  )

  const handleBlur: NonNullable<ComponentProps<typeof BirthdateInput>['onBlur']> = useEvent(
    (...args) => {
      if (sardineId) {
        void trackFocusChange(sardineId, false)
      }
      onBlur?.(...args)
      field.onBlur()
    },
  )

  return (
    <BirthdateInput
      {...props}
      {...(sardineId ? { dataSet: { [sardineIdKey]: sardineId } } : {})}
      value={field.value}
      info={{
        ...info,
        error: formState.isValidating ? undefined : fieldState.error?.message,
      }}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      ref={localRef}
    />
  )
}) as FormInputType<ComponentRef<typeof BirthdateInput>>
